import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Plus, Play, CheckCircle, X, ChevronUp, ChevronDown, Edit, Trash2, UserPlus, UserMinus, MapPin, Wand2, AlertTriangle } from 'lucide-react';
import { API_URL } from '../config';

// Couleurs par catégorie
const CATEGORIE_COLORS = {
  logistique_depart: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 border-amber-300',
  trajet_aller: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 border-cyan-300 dark:border-cyan-700',
  dechargement: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700',
  mise_en_place: 'bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 border-violet-300 dark:border-violet-700',
  accueil: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700',
  service_cocktail: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 border-emerald-300',
  service_entree: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 border-emerald-300',
  service_plat: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 border-emerald-300',
  service_dessert: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 border-emerald-300',
  service_cafe: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700',
  demontage: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 border-orange-300',
  nettoyage: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 border-orange-300 dark:border-orange-700',
  chargement_retour: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700',
  trajet_retour: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 border-cyan-300 dark:border-cyan-700',
  autre: 'bg-elevated-1 text-text-primary border-border-strong',
};

// Couleurs par statut
const STATUT_STYLES = {
  a_venir: 'bg-elevated-1 border-border-strong',
  en_cours: 'bg-blue-50 dark:bg-blue-950/40 border-blue-500 dark:border-blue-500 animate-pulse',
  termine: 'bg-green-50 dark:bg-green-950/40 border-green-500 dark:border-green-500',
  annule: 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-700 opacity-60',
};

const STATUT_ICONS = {
  a_venir: <span className="w-3 h-3 rounded-full bg-gray-400 inline-block" />,
  en_cours: <span className="w-3 h-3 rounded-full bg-blue-500 inline-block animate-pulse" />,
  termine: <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />,
  annule: <X className="w-4 h-4 text-red-500" />,
};

const CATEGORIES = [
  { value: 'logistique_depart', label: 'Logistique (départ)' },
  { value: 'trajet_aller', label: 'Trajet aller' },
  { value: 'dechargement', label: 'Déchargement' },
  { value: 'mise_en_place', label: 'Mise en place' },
  { value: 'accueil', label: 'Accueil' },
  { value: 'service_cocktail', label: 'Service cocktail' },
  { value: 'service_entree', label: 'Service entrée' },
  { value: 'service_plat', label: 'Service plat' },
  { value: 'service_dessert', label: 'Service dessert' },
  { value: 'service_cafe', label: 'Service café' },
  { value: 'demontage', label: 'Démontage' },
  { value: 'nettoyage', label: 'Nettoyage' },
  { value: 'chargement_retour', label: 'Chargement retour' },
  { value: 'trajet_retour', label: 'Trajet retour' },
  { value: 'autre', label: 'Autre' },
];

const WhatsAppIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

const EventTimeline = ({ eventId, authUser, authToken, employees = [], isChefProjet = false, readOnly = false }) => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [showStaffModal, setShowStaffModal] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    titre: '', heure_debut: '', heure_fin: '', categorie: 'autre', description: '', responsable_id: '', ordre: 0
  });

  const isAdmin = authUser && ['Patron', 'Super Manager'].includes(authUser.role_name);
  const canEdit = isAdmin || isChefProjet;

  const loadTimeline = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/timeline`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!res.ok) throw new Error('Erreur chargement');
      setSteps(await res.json());
    } catch (err) {
      console.error('Erreur chargement timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, authToken]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  const handleGenerate = async () => {
    if (!window.confirm('Générer un template de timeline automatique ?')) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/timeline/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Erreur génération');
        return;
      }
      await loadTimeline();
    } catch (err) {
      alert('Erreur : ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveStep = async () => {
    if (!formData.titre || !formData.heure_debut) {
      alert('Titre et heure de début requis');
      return;
    }
    try {
      const url = editingStep
        ? `${API_URL}/evenements/${eventId}/timeline/${editingStep.id}`
        : `${API_URL}/evenements/${eventId}/timeline`;
      const res = await fetch(url, {
        method: editingStep ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) { const err = await res.json(); alert(err.error); return; }
      setShowAddModal(false);
      setEditingStep(null);
      setFormData({ titre: '', heure_debut: '', heure_fin: '', categorie: 'autre', description: '', responsable_id: '', ordre: 0 });
      await loadTimeline();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Supprimer cette étape ?')) return;
    try {
      await fetch(`${API_URL}/evenements/${eventId}/timeline/${stepId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      await loadTimeline();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const handleStatusChange = async (stepId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/timeline/${stepId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatus })
      });
      if (!res.ok) { const err = await res.json(); alert(err.error); return; }
      await loadTimeline();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const handleAddStaff = async (stepId, employeeId, poste) => {
    try {
      await fetch(`${API_URL}/evenements/${eventId}/timeline/${stepId}/staff`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: employeeId, poste })
      });
      await loadTimeline();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const handleRemoveStaff = async (stepId, employeeId) => {
    try {
      await fetch(`${API_URL}/evenements/${eventId}/timeline/${stepId}/staff/${employeeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      await loadTimeline();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const openEditModal = (step) => {
    setEditingStep(step);
    setFormData({
      titre: step.titre, heure_debut: step.heure_debut, heure_fin: step.heure_fin || '',
      categorie: step.categorie, description: step.description || '',
      responsable_id: step.responsable_id || '', ordre: step.ordre
    });
    setShowAddModal(true);
  };

  // Progression globale
  const totalSteps = steps.filter(s => s.statut !== 'annule').length;
  const doneSteps = steps.filter(s => s.statut === 'termine').length;
  const progressPct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;

  const shareTimelineWhatsApp = () => {
    if (steps.length === 0) { alert('Aucune étape à partager'); return; }
    const catLabels = {};
    CATEGORIES.forEach(c => { catLabels[c.value] = c.label; });
    let msg = `*Déroulé Jour J*\n`;
    msg += `${doneSteps}/${totalSteps} étapes terminées (${progressPct}%)\n\n`;
    steps.forEach((s, i) => {
      const status = s.statut === 'termine' ? '✅' : s.statut === 'en_cours' ? '🔵' : s.statut === 'annule' ? '❌' : '⬜';
      const time = s.heure_debut ? (s.heure_fin ? `${s.heure_debut} → ${s.heure_fin}` : s.heure_debut) : '';
      const cat = catLabels[s.categorie] || '';
      msg += `${status} *${s.titre}*`;
      if (cat) msg += ` [${cat}]`;
      if (time) msg += `\n   🕐 ${time}`;
      if (s.responsable_first_name) msg += `\n   👤 ${s.responsable_first_name} ${s.responsable_last_name || ''}`;
      if (s.staff && s.staff.length > 0) {
        msg += `\n   👥 ${s.staff.map(st => `${st.first_name} ${st.last_name || ''}`).join(', ')}`;
      }
      if (s.description) msg += `\n   📝 ${s.description}`;
      msg += '\n\n';
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) return <div className="text-center py-8 text-text-muted">Chargement du déroulé...</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold text-text-primary">Déroulé Jour J</h3>
        <div className="flex gap-2">
          {steps.length > 0 && (
            <button
              onClick={shareTimelineWhatsApp}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 transition"
            >
              <WhatsAppIcon /> WhatsApp
            </button>
          )}
          {canEdit && !readOnly && (
            <>
              <button
                onClick={() => { setEditingStep(null); setFormData({ titre: '', heure_debut: '', heure_fin: '', categorie: 'autre', description: '', responsable_id: '', ordre: steps.length + 1 }); setShowAddModal(true); }}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
              >
                <Plus className="w-4 h-4" /> Étape
              </button>
              {steps.length === 0 && isAdmin && (
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 transition disabled:opacity-50"
                >
                  <Wand2 className="w-4 h-4" /> {generating ? 'Génération...' : 'Générer template'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Barre de progression */}
      {steps.length > 0 && (
        <div className="bg-surface rounded-lg p-3 border">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-text-secondary">Progression</span>
            <span className="font-bold text-text-primary">{doneSteps}/{totalSteps} ({progressPct}%)</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${progressPct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Liste des étapes */}
      {steps.length === 0 ? (
        <div className="text-center py-8 bg-elevated-1 rounded-xl border-2 border-dashed border-border-strong">
          <Clock className="w-12 h-12 text-text-muted mx-auto mb-2" />
          <p className="text-text-muted">Aucune étape définie</p>
          {canEdit && <p className="text-sm text-text-muted mt-1">Cliquez sur "Générer template" pour créer un déroulé type</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {steps.map((step, idx) => {
            const catColor = CATEGORIE_COLORS[step.categorie] || CATEGORIE_COLORS.autre;
            const statusStyle = STATUT_STYLES[step.statut] || STATUT_STYLES.a_venir;
            const responsable = step.responsable_first_name
              ? `${step.responsable_first_name} ${step.responsable_last_name}`
              : null;

            return (
              <div key={step.id} className={`rounded-lg border-l-4 p-3 sm:p-4 ${statusStyle} ${step.statut === 'annule' ? 'line-through' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {STATUT_ICONS[step.statut]}
                      <span className="font-bold text-text-primary">{step.titre}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catColor}`}>
                        {CATEGORIES.find(c => c.value === step.categorie)?.label || step.categorie}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-text-secondary flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {step.heure_debut}{step.heure_fin ? ` → ${step.heure_fin}` : ''}
                      </span>
                      {responsable && (
                        <span className="flex items-center gap-1 text-violet-600 dark:text-violet-400 font-medium">
                          Resp: {responsable}
                        </span>
                      )}
                    </div>

                    {step.description && (
                      <p className="text-sm text-text-muted mt-1">{step.description}</p>
                    )}

                    {/* Staff affecté */}
                    {step.staff && step.staff.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <span className="text-xs text-text-muted">Personnel:</span>
                        {step.staff.map(s => (
                          <span key={s.employee_id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface rounded-full text-xs border">
                            <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] flex items-center justify-center font-bold">
                              {s.avatar || (s.first_name?.charAt(0) + s.last_name?.charAt(0))}
                            </span>
                            {s.first_name} {s.last_name?.charAt(0)}.
                            {s.poste && <span className="text-text-muted">({s.poste})</span>}
                            {canEdit && !readOnly && (
                              <button onClick={() => handleRemoveStaff(step.id, s.employee_id)} className="text-red-400 hover:text-red-600 dark:text-red-400 ml-0.5">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Infos complétion */}
                    {step.statut === 'termine' && step.completed_at && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        Terminé le {new Date(step.completed_at).toLocaleString('fr-FR')}
                        {step.completed_by_first_name && ` par ${step.completed_by_first_name} ${step.completed_by_last_name}`}
                      </p>
                    )}

                    {step.notes_terrain && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 italic">Note terrain : {step.notes_terrain}</p>
                    )}
                  </div>

                  {/* Actions */}
                  {!readOnly && (
                    <div className="flex flex-col gap-1 shrink-0">
                      {step.statut === 'a_venir' && canEdit && (
                        <button onClick={() => handleStatusChange(step.id, 'en_cours')}
                          className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition"
                          title="Démarrer">
                          <Play className="w-3 h-3" /> Go
                        </button>
                      )}
                      {step.statut === 'en_cours' && (
                        <button onClick={() => handleStatusChange(step.id, 'termine')}
                          className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition"
                          title="Terminer">
                          <CheckCircle className="w-3 h-3" /> Fait
                        </button>
                      )}
                      {canEdit && (
                        <>
                          <button onClick={() => setShowStaffModal(step.id)}
                            className="p-1 text-violet-600 hover:bg-violet-50 dark:bg-violet-950/40 rounded transition" title="Affecter personnel">
                            <UserPlus className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEditModal(step)}
                            className="p-1 text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 rounded transition" title="Modifier">
                            <Edit className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDeleteStep(step.id)}
                              className="p-1 text-red-500 hover:bg-red-50 dark:bg-red-950/40 rounded transition" title="Supprimer">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modale ajout/édition d'étape */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h3 className="text-lg font-bold text-text-primary">{editingStep ? 'Modifier l\'étape' : 'Nouvelle étape'}</h3>
              <button onClick={() => { setShowAddModal(false); setEditingStep(null); }} className="p-2 hover:bg-elevated-1 rounded-full transition">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Titre *</label>
                <input type="text" value={formData.titre} onChange={e => setFormData({...formData, titre: e.target.value})}
                  className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg" placeholder="Ex: Service cocktail" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Heure début *</label>
                  <input type="time" value={formData.heure_debut} onChange={e => setFormData({...formData, heure_debut: e.target.value})}
                    className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Heure fin</label>
                  <input type="time" value={formData.heure_fin} onChange={e => setFormData({...formData, heure_fin: e.target.value})}
                    className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Catégorie</label>
                <select value={formData.categorie} onChange={e => setFormData({...formData, categorie: e.target.value})}
                  className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Responsable</label>
                <select value={formData.responsable_id} onChange={e => setFormData({...formData, responsable_id: e.target.value ? parseInt(e.target.value) : ''})}
                  className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg">
                  <option value="">— Aucun —</option>
                  {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg" rows={2} placeholder="Notes..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Ordre</label>
                <input type="number" value={formData.ordre} onChange={e => setFormData({...formData, ordre: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg" />
              </div>
            </div>
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button onClick={() => { setShowAddModal(false); setEditingStep(null); }}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border hover:bg-elevated-1 transition">Annuler</button>
              <button onClick={handleSaveStep}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-blue-600 text-white hover:bg-blue-700 transition">
                {editingStep ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale affectation de personnel */}
      {showStaffModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h3 className="text-lg font-bold text-text-primary">Affecter du personnel</h3>
              <button onClick={() => setShowStaffModal(null)} className="p-2 hover:bg-elevated-1 rounded-full transition">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-2">
              {employees.map(emp => {
                const step = steps.find(s => s.id === showStaffModal);
                const isAffected = step?.staff?.some(s => s.employee_id === emp.id);
                return (
                  <div key={emp.id} className={`flex items-center justify-between p-3 rounded-lg border min-h-[44px] ${isAffected ? 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800' : 'hover:bg-elevated-1'}`}>
                    <span className="text-base sm:text-sm">{emp.first_name} {emp.last_name}</span>
                    {isAffected ? (
                      <button onClick={() => handleRemoveStaff(showStaffModal, emp.id)}
                        className="flex items-center gap-1 px-3 py-2 sm:py-1 bg-red-100 dark:bg-red-900/40 text-red-700 rounded text-sm sm:text-xs hover:bg-red-200 dark:bg-red-800/40 transition min-h-[44px] sm:min-h-0">
                        <UserMinus className="w-3 h-3" /> Retirer
                      </button>
                    ) : (
                      <button onClick={() => handleAddStaff(showStaffModal, emp.id, null)}
                        className="flex items-center gap-1 px-3 py-2 sm:py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 rounded text-sm sm:text-xs hover:bg-blue-200 dark:bg-blue-800/40 transition min-h-[44px] sm:min-h-0">
                        <UserPlus className="w-3 h-3" /> Affecter
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button onClick={() => setShowStaffModal(null)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border hover:bg-elevated-1 transition">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventTimeline;
